import { HeaderChip } from '@/components/blocks/HeaderChip';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/ui/cn';
import type { BlockButtonProps } from '@/types/blocks';
import type { ComponentClinicProps } from '@/types/components';
import Image from 'next/image';
import Link from 'next/link';

function Cta({ button }: { button: BlockButtonProps }) {
  const className = cn(
    'h-12 w-full rounded-[50px] px-5 text-[18px] leading-[130%] font-bold opacity-100 shadow-none',
    'lg:h-15 lg:min-w-0 lg:flex-1 lg:basis-0 lg:rounded-[30px] lg:px-3 lg:text-[21px]',
    'text-center whitespace-nowrap xl:px-[30px]',
    button.theme === 'light' && 'border-sh-gray border bg-white/80',
    button.theme === 'dark' && 'bg-sh-dark-gray text-sh-paper'
  );

  return button.link ? (
    <Button variant={button.theme} asChild className={className}>
      <Link href={button.link}>{button.text}</Link>
    </Button>
  ) : (
    <Button variant={button.theme} className={className}>
      {button.text}
    </Button>
  );
}

export function LocationCard({
  name,
  image,
  address,
  telephone,
  openingHours,
  servicesSummary,
  badgeChip,
  primaryButton,
  secondaryButton,
  className,
}: ComponentClinicProps) {
  return (
    <article
      data-component='location-card'
      data-testid='location-card'
      className={cn(
        'border-sh-gray flex w-full flex-col overflow-hidden rounded-3xl border bg-white',
        'lg:flex-row lg:items-start lg:gap-8 lg:p-4',
        className
      )}
    >
      {image && (
        <div className='relative h-50 w-full flex-none overflow-hidden lg:aspect-square lg:h-auto lg:w-[44.8%] lg:max-w-92 lg:self-stretch lg:rounded-3xl'>
          <Image
            src={image.url}
            alt={image.alt || name || ''}
            fill
            sizes='(min-width: 1024px) 368px, 100vw'
            className='object-cover'
          />
        </div>
      )}

      <div className='flex min-w-0 flex-1 flex-col gap-4 p-4 lg:self-stretch lg:p-0'>
        <div className='flex flex-col items-start gap-2 pb-4 text-[18px] leading-[130%] text-black lg:text-[21px]'>
          {name && (
            <h3 className='text-[24px] leading-[100%] font-bold tracking-[-0.02em] lg:text-[32px] lg:leading-[130%] lg:tracking-[-0.04em]'>
              {name}
            </h3>
          )}
          {badgeChip && (
            <HeaderChip
              {...badgeChip}
              className='h-7 px-4 text-black lg:h-8 [&_span]:leading-[130%] lg:[&_span]:text-[21px]'
            />
          )}
          {address && <p className='whitespace-pre-line'>{address}</p>}
          {telephone && (
            <p>
              <a href={`tel:${telephone}`}>{telephone}</a>
            </p>
          )}
          {openingHours && <p>{openingHours}</p>}
          {servicesSummary && <p>{servicesSummary}</p>}
        </div>

        {(primaryButton || secondaryButton) && (
          <div className='mt-auto flex w-full flex-col gap-4 lg:flex-row'>
            {primaryButton && <Cta button={primaryButton} />}
            {secondaryButton && <Cta button={secondaryButton} />}
          </div>
        )}
      </div>
    </article>
  );
}
